import { useLingui } from "@lingui/react/macro";
import { Bot } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCookingSessionCapacity } from "@/hooks/cooking-sessions/queries";
import type { CapacityStatus, CookingSessionSource } from "@/types/cooking-session";

interface PrepCapacityCheckProps {
  locationId?: number;
  source?: CookingSessionSource;
}

const STATUS_DOT: Record<CapacityStatus, string> = {
  on_track: "bg-(--color-success)",
  at_risk: "bg-(--color-warning)",
  behind: "bg-(--color-danger)",
  no_chefs: "bg-(--color-text-secondary)",
};

// Subtle badge: light tint + dark saturated text in light, dark tint + bright
// text in dark (the readable, standard soft-badge pattern).
const STATUS_BADGE: Record<CapacityStatus, string> = {
  on_track: "bg-(--color-success-bg) text-(--color-success-text) border-(--color-success-text)/20 hover:bg-(--color-success)/25",
  at_risk: "bg-(--color-warning-bg) text-(--color-warning-text) border-(--color-warning-text)/20 hover:bg-(--color-warning)/25",
  behind: "bg-(--color-danger-bg) text-(--color-danger-text) border-(--color-danger-text)/20 hover:bg-(--color-danger)/25",
  // Not a warning. Nobody is on the line, so there is nothing to be at risk of.
  no_chefs: "bg-(--color-surface-muted) text-(--color-text-secondary) border-(--color-border)",
};

interface MetricCellProps {
  label: string;
  value: number | string;
  unit?: string;
}

function MetricCell({ label, value, unit }: MetricCellProps) {
  const isText = typeof value === "string";
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-(--color-text-primary)/40">{label}</span>
      <div className="flex items-baseline gap-1">
        <span
          className={
            isText
              ? "text-sm leading-snug text-(--color-text-secondary)"
              : "text-[28px] font-medium leading-none text-(--color-text-primary)"
          }
        >
          {value}
        </span>
        {unit && <span className="text-xs text-(--color-text-primary)/40">{unit}</span>}
      </div>
    </div>
  );
}

export function PrepCapacityCheck({ locationId, source }: PrepCapacityCheckProps) {
  const { t } = useLingui();
  const { data } = useCookingSessionCapacity({ locationId, source });

  const status = data?.status ?? "on_track";

  const STATUS_LABELS: Record<CapacityStatus, string> = {
    on_track: t`On track`,
    at_risk: t`At risk`,
    behind: t`Behind`,
    no_chefs: t`No chefs assigned`,
  };
  const bufferMinutes = data?.bufferMinutes ?? 0;

  return (
    <div className="flex-1 min-w-0 bg-(--color-surface-content) border-[0.5px] border-(--color-border) rounded-xl p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-(--color-text-primary)">
          <Bot className="h-5 w-5 text-(--color-surface-page)" />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-(--color-text-primary)">{t`Prep capacity check`}</span>
            <Badge
              className={`flex items-center gap-1.5 ${STATUS_BADGE[status]} border rounded-full px-2.5 py-0.5 text-xs font-medium`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status]}`} />
              {STATUS_LABELS[status]}
            </Badge>
          </div>
          {data && (
            <p className="text-sm text-(--color-text-primary)/60">
              {status === "no_chefs"
                ? t`Nobody is on the line, so there is no capacity to measure against. Assign a chef to see whether the queue fits.`
                : status === "on_track"
                ? t`Capacity looks OK — you have ~${bufferMinutes} minutes of buffer.`
                : status === "at_risk"
                ? t`Capacity is tight — only ~${bufferMinutes} minutes of buffer.`
                : t`Capacity exceeded — you are behind by ${Math.abs(bufferMinutes)} minutes.`}
            </p>
          )}
          {data && (
            <p className="text-xs text-(--color-text-primary)/50">
              {t`Scheduled:`}{" "}
              <span className="font-medium text-(--color-text-primary)">{t`${data.scheduledMinutes} min`}</span>
              {" • "}
              {t`On the line:`}{" "}
              <span className="font-medium text-(--color-text-primary)">
                {t`${data.chefs} for ${Math.round(data.windowMinutes / 60)}h`}
              </span>
              {/* Nothing finished, nothing measured. "Efficiency: 100%" beside
                  "At risk" was a hard-coded literal under a footer claiming the
                  number was measured (T-1104). */}
              {data.efficiency !== null && (
                <>
                  {" • "}
                  {t`Efficiency:`}{" "}
                  <span className="font-medium text-(--color-text-primary)">{data.efficiency}%</span>
                </>
              )}
            </p>
          )}
          {data && (
            <div className="flex items-center gap-1.5 text-xs text-(--color-text-primary)/50">
              {t`Includes cooking now:`}
              <Badge className="bg-(--color-success-bg) text-(--color-success-text) border border-(--color-success-text)/20 hover:bg-(--color-success)/25 rounded-full px-2 py-0 text-xs font-medium h-5">
                {data.includeCookingNow ? t`Yes` : t`No`}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Metrics grid */}
      {data && (
        <div className="grid grid-cols-4 gap-4 border-t border-(--color-border) pt-4">
          <MetricCell label={t`Required`} value={data.required} unit={t`min`} />
          <MetricCell label={t`Capacity`} value={data.capacity} unit={t`min`} />
          <MetricCell label={t`Utilization`} value={data.utilization} unit="%" />
          {data.efficiency === null ? (
            <MetricCell label={t`Efficiency`} value={t`Not yet measured`} />
          ) : (
            <MetricCell label={t`Efficiency`} value={data.efficiency} unit="%" />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-start gap-2 bg-(--color-surface-muted) rounded-lg px-3 py-2">
        <span className="text-xs text-(--color-text-primary)/40 mt-px">ℹ</span>
        <p className="text-xs text-(--color-text-primary)/50">
          {t`Capacity is the chefs on the line times the length of the service. Required is what the queue still needs. Efficiency is target time over actual, across what has finished.`}
        </p>
      </div>
    </div>
  );
}
